import { Box, SxProps, Typography, TypographyPropsVariantOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { RetroButton } from "components/RetroButton";
import { Row } from "./Containers";
import { Variant } from "@mui/material/styles/createTypography";

interface BaseButtonProps {
  children: React.ReactNode | null;
  variant?: "primary" | "white" | "dark" | "disabled" | "green" | "red";
  onClick?: () => void;
  disabled?: boolean;
  outerSx?: SxProps;
  fullWidth?: boolean;
}

const BaseButton = ({
  variant = "primary",
  onClick = () => {},
  disabled,
  outerSx = {},
  fullWidth = false,
  children,
  ...props
}: BaseButtonProps) => {
  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "fit-content",
        ...outerSx,
      }}>
      <RetroButton variant={variant} onClick={onClick} disabled={disabled} {...props}>
        {children}
      </RetroButton>
    </Box>
  );
};

function withLoading<T extends BaseButtonProps>(
  Component: React.ComponentType<T>,
  LoadingComponent: React.ReactNode,
): React.FC<T & { loading: boolean }> {
  return ({ loading, children, ...props }: T & { loading: boolean }) => {
    const isDisabled = loading || (props.disabled ?? false);
    return (
      <Component {...(props as unknown as T)} disabled={isDisabled}>
        {loading ? LoadingComponent : children}
      </Component>
    );
  };
}

const TextButton = ({
  variant = "primary",
  typo = "button-lg",
  typoColor = "inherit",
  onClick = () => {},
  disabled,
  outerSx = {},
  innerSx = {
    padding: "12px 24px",
  },
  children = null,
  ...props
}: {
  innerSx?: SxProps;
  variant?: "primary" | "white" | "dark" | "disabled" | "green" | "red";
  typo?: OverridableStringUnion<Variant | "inherit", TypographyPropsVariantOverrides> | undefined;
  typoColor?: string;
} & BaseButtonProps) => {
  return (
    <BaseButton
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      outerSx={outerSx}
      fullWidth={!!props.fullWidth}
      {...props}>
      <Row justifyContent="center" alignItems="center" sx={innerSx} fullWidth={!!props.fullWidth}>
        <Typography variant={typo} color={typoColor}>
          {children}
        </Typography>
      </Row>
    </BaseButton>
  );
};

const LoadingTextButton = withLoading(TextButton, "Loading...");
const LoadingButton = ({
  loadingComponent,
  ...props
}: {
  loadingComponent: React.ReactNode;
} & BaseButtonProps) => {
  return withLoading(BaseButton, loadingComponent)({ ...props, loading: true });
};

export { BaseButton, TextButton, LoadingTextButton, withLoading };
